// lib/koi-pond-v2/geometry/koi.ts
//
// Procedural koi body geometry. Stylized fish silhouette: rounded head
// tapering to a forked tail. ~434 triangles target. Body axis along +X
// (head at +X, tail at -X).
//
// UV layout — critical for Phase 2's koi pattern shader to land
// markings correctly:
//   Body dorsal hemisphere → U: [0, 0.8], V: [0, 0.5]
//   Body ventral hemisphere→ U: [0, 0.8], V: [0.5, 1.0]
//   Tail strip             → U: [0.8, 1.0], V: [0, 1.0]
//
// The pattern shader's "head end" of UV is U=0 (matches +X body axis).
// Top of dorsal at V=0 is where the kohaku amber patches land naturally
// when the camera is overhead (Phase 2's pond camera angle).
//
// Builder is parameterized for future tuning but defaults match the
// Phase 1 acceptance spec. No external .glb dependency.

import { BufferGeometry, BufferAttribute } from 'three';

export type KoiGeometryOpts = {
  bodyLength?: number;     // total head→tail length in world units (default 1.0)
  bodyMaxWidth?: number;   // widest cross-section radius (default 0.18)
  bodySegments?: number;   // segments along body axis (default 18)
  radialSegments?: number; // segments around body circumference (default 12)
  tailSpread?: number;     // tail fork spread (default 0.18)
};

export function makeKoiGeometry(opts: KoiGeometryOpts = {}): BufferGeometry {
  const length = opts.bodyLength ?? 1.0;
  const maxR = opts.bodyMaxWidth ?? 0.18;
  const bodySegs = opts.bodySegments ?? 18;
  const radSegs = opts.radialSegments ?? 12;
  const tailSpread = opts.tailSpread ?? 0.18;

  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];

  // Body taper: peaks ~30% from head, narrows to tail
  function bodyRadius(t: number): number {
    // t in [0, 1], 0 = head tip, 1 = tail base
    if (t < 0.05) return maxR * (t / 0.05) * 0.6; // head taper
    if (t < 0.3) return maxR * (0.6 + 0.4 * ((t - 0.05) / 0.25)); // head→peak
    return maxR * (1.0 - 0.55 * ((t - 0.3) / 0.7)); // peak→tail base
  }

  // BODY: ring loop, head at +X (t=0), tail-base at -X (t=1)
  for (let i = 0; i <= bodySegs; i++) {
    const t = i / bodySegs;
    const x = length * 0.5 - t * length * 0.85;
    const r = bodyRadius(t);
    for (let j = 0; j <= radSegs; j++) {
      const angle = (j / radSegs) * Math.PI * 2;
      const y = Math.sin(angle) * r * 0.85; // slightly flatter top-bottom
      const z = Math.cos(angle) * r;
      positions.push(x, y, z);
      // Approximate normal — outward from spine
      const nLen = Math.sqrt(y * y + z * z) || 1;
      normals.push(0, y / nLen, z / nLen);
      // UV: dorsal half (y > 0) maps to V in [0, 0.5]; ventral V in [0.5, 1.0]
      // U runs head→tail through [0, 0.8]
      const u = t * 0.8;
      // Convert angle to V: top of body (angle=π/2) = V0; bottom = V1
      const v = (1.0 - Math.sin(angle) * 0.5 - 0.5) * 0.5 + (angle > 0 && angle < Math.PI ? 0 : 0.5);
      uvs.push(u, v);
    }
  }
  // Body indices. Winding is CCW from outside the body so the triangles
  // are front-facing under Three.js's default FrontSide culling. The
  // previous winding (a, c, b) / (b, c, d) was CW from outside, which
  // caused the body to be culled entirely on the /style-guide preview —
  // only the tail (a flat plane facing the camera) rendered. The
  // explicit per-vertex normals above already point outward from the
  // spine, so this winding fix is the only change needed.
  const ringSize = radSegs + 1;
  for (let i = 0; i < bodySegs; i++) {
    for (let j = 0; j < radSegs; j++) {
      const a = i * ringSize + j;
      const b = a + 1;
      const c = a + ringSize;
      const d = c + 1;
      indices.push(a, b, c);
      indices.push(b, d, c);
    }
  }

  // TAIL: two triangular fins fanning back from tail base
  const tailBaseX = length * 0.5 - length * 0.85;
  const tailTipX = tailBaseX - length * 0.15;
  const baseIdx = positions.length / 3;
  // Top fork
  positions.push(tailBaseX, 0, 0); // anchor
  positions.push(tailTipX, tailSpread, 0); // top tip
  positions.push(tailTipX - 0.02, 0, 0); // center notch
  // Bottom fork
  positions.push(tailTipX, -tailSpread, 0); // bottom tip
  for (let k = 0; k < 4; k++) normals.push(0, 1, 0); // simple up-normals for tail
  // Tail UV: U in [0.8, 1.0], V along [0, 1]
  uvs.push(0.8, 0.5);  // anchor
  uvs.push(1.0, 0.0);  // top tip
  uvs.push(0.9, 0.5);  // notch
  uvs.push(1.0, 1.0);  // bottom tip
  indices.push(baseIdx, baseIdx + 1, baseIdx + 2);
  indices.push(baseIdx, baseIdx + 2, baseIdx + 3);

  const geom = new BufferGeometry();
  geom.setAttribute('position', new BufferAttribute(new Float32Array(positions), 3));
  geom.setAttribute('normal', new BufferAttribute(new Float32Array(normals), 3));
  geom.setAttribute('uv', new BufferAttribute(new Float32Array(uvs), 2));
  geom.setIndex(indices);
  geom.computeBoundingBox();
  geom.computeBoundingSphere();
  return geom;
}
